import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Paper, Tab,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, TextField, Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useToast } from '../../context/ToastContext';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { AuthService } from '../../services/auth.service';
import { PaymentLinkService } from '../../services/paymentLink.service';
import { PaymentAttempt, PaymentLink, PaymentLinkStatus } from '../../interfaces/sales/paymentLink.interface';
import { formatDateTime } from '../../utils/dateUtils';
import { formatMoney, humanizeEnum } from '../../utils/format';

const STATUS_COLOR: Record<string, 'default' | 'success' | 'warning' | 'error' | 'info'> = {
  OPEN: 'info', PAID: 'success', EXPIRED: 'default', CANCELLED: 'default', COMPLETED: 'success',
  RESOLVED_MANUALLY: 'success', FAILED: 'error', NEEDS_RECONCILIATION: 'warning', VERIFYING: 'info', INITIATED: 'default',
};

const STALE_INITIATED_MS = 5 * 60 * 1000;

/** W3-T: payment links of invoices and the attempts that need a person (verification pending or unreconciled). */
const PaymentLinks: React.FC = () => {
  const institutionId = AuthService.getSelectedInstitution() as string;
  const toast = useToast();
  const canManage = usePermission(PERMISSIONS.SALES_INVOICE_MANAGE);
  const canVerify = usePermission(PERMISSIONS.RECEIPT_MANAGE);
  const [tab, setTab] = useState<'links' | 'attempts'>('links');
  const [status, setStatus] = useState<PaymentLinkStatus | ''>('');
  const [links, setLinks] = useState<PaymentLink[]>([]);
  const [attempts, setAttempts] = useState<PaymentAttempt[]>([]);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState<PaymentLink | null>(null);
  const [reason, setReason] = useState('');
  const [resolving, setResolving] = useState<PaymentAttempt | null>(null);
  const [resolveReceiptId, setResolveReceiptId] = useState('');
  const [resolveNote, setResolveNote] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [p, parked, started, verifying] = await Promise.all([
        PaymentLinkService.list(institutionId, status || undefined, 0, 100),
        PaymentLinkService.attempts(institutionId, 'NEEDS_RECONCILIATION'),
        PaymentLinkService.attempts(institutionId, 'INITIATED'),
        PaymentLinkService.attempts(institutionId, 'VERIFYING'),
      ]);
      setLinks(p.content);
      // a session still with the payer is not "stuck": only INITIATED attempts older than the stale window are shown
      const staleBefore = Date.now() - STALE_INITIATED_MS;
      const stuck = started.filter(x => x.createdAt && new Date(x.createdAt).getTime() < staleBefore);
      const seen = new Set<string>();
      setAttempts([...parked, ...stuck, ...verifying].filter(x => !seen.has(x.id) && !!seen.add(x.id)));
    } catch (e) {
      toast.showApiError(e, 'Failed to load payment links');
    } finally {
      setLoading(false);
    }
  }, [institutionId, status]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const cancel = async () => {
    if (!cancelling) return;
    try {
      await PaymentLinkService.cancel(cancelling.id, reason || undefined);
      setCancelling(null); setReason('');
      toast.showSuccess('Payment link cancelled');
      await load();
    } catch (e) {
      toast.showApiError(e, 'Failed to cancel the link');
    }
  };
  const resolveManually = async () => {
    if (!resolving) return;
    if (!resolveReceiptId.trim() && !resolveNote.trim()) {
      toast.showError('Give the manual receipt id, or a note saying what happened (refunded / rejected)');
      return;
    }
    try {
      await PaymentLinkService.resolve(resolving.id, {
        receiptId: resolveReceiptId.trim() || undefined, note: resolveNote.trim() || undefined,
      });
      setResolving(null); setResolveReceiptId(''); setResolveNote('');
      toast.showSuccess('Marked as resolved');
      await load();
    } catch (e) {
      toast.showApiError(e, 'Failed to mark resolved');
    }
  };
  const reverify = async (a: PaymentAttempt) => {
    try {
      const r = await PaymentLinkService.reverify(a.id);
      toast.showSuccess(`Re-checked with the gateway: ${humanizeEnum(r.outcome)}`);
      await load();
    } catch (e) {
      toast.showApiError(e, 'Failed to re-verify');
    }
  };

  return (
    <Box sx={{ p: 3 }} data-help-page="sales.paymentLinks.page">
      <Typography variant="h5" gutterBottom>Payment Links</Typography>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab value="links" label="Links" data-help="sales.paymentLinks.tab.links" />
        <Tab value="attempts" label={`Needs attention (${attempts.length})`} data-help="sales.paymentLinks.tab.attempts" />
      </Tabs>
      {loading ? <CircularProgress /> : tab === 'links' ? (
        <>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }} data-help-section="sales.paymentLinks.filters">
            <TextField select size="small" data-help="sales.paymentLinks.status" label="Status" value={status} sx={{ flex: '1 1 160px', maxWidth: 220 }}
              onChange={e => setStatus(e.target.value as PaymentLinkStatus | '')}>
              <MenuItem value="">All</MenuItem>
              {['OPEN', 'PAID', 'EXPIRED', 'CANCELLED'].map(s => <MenuItem key={s} value={s}>{humanizeEnum(s)}</MenuItem>)}
            </TextField>
          </Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            A link's address is shown only when it is created. To send it again, cancel the link and create a new one from the invoice.
          </Alert>
          <TableContainer component={Paper} data-help-section="sales.paymentLinks.linksTable">
            <Table size="small">
              <TableHead><TableRow>
                <TableCell data-help="sales.paymentLinks.col.invoice">Invoice</TableCell><TableCell data-help="sales.paymentLinks.col.customer">Customer</TableCell><TableCell align="right" data-help="sales.paymentLinks.col.amount">Amount</TableCell>
                <TableCell data-help="sales.paymentLinks.col.status">Status</TableCell><TableCell data-help="sales.paymentLinks.col.expires">Expires</TableCell><TableCell data-help="sales.paymentLinks.col.sentTo">Sent to</TableCell><TableCell /></TableRow></TableHead>
              <TableBody>
                {links.length === 0 && <TableRow><TableCell colSpan={7} align="center">No payment links.</TableCell></TableRow>}
                {links.map(l => (
                  <TableRow key={l.id}>
                    <TableCell><RouterLink to={`/sales/invoices/${l.salesInvoiceId}`}>{l.invoiceNumber ?? 'Invoice'}</RouterLink></TableCell>
                    <TableCell>{l.customerName}</TableCell>
                    <TableCell align="right">{formatMoney(l.amount)}</TableCell>
                    <TableCell><Chip size="small" label={humanizeEnum(l.status)} color={STATUS_COLOR[l.status]} /></TableCell>
                    <TableCell>{formatDateTime(l.expiresAt)}</TableCell>
                    <TableCell>{l.sentTo ?? '-'}</TableCell>
                    <TableCell align="right">
                      {canManage && l.status === 'OPEN' && <Button size="small" color="error" data-help="sales.paymentLinks.action.cancel" onClick={() => setCancelling(l)}>Cancel</Button>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      ) : (
        <TableContainer component={Paper} data-help-section="sales.paymentLinks.attemptsTable">
          <Table size="small">
            <TableHead><TableRow>
              <TableCell data-help="sales.paymentLinks.attempt.col.invoice">Invoice</TableCell><TableCell data-help="sales.paymentLinks.attempt.col.provider">Provider</TableCell><TableCell data-help="sales.paymentLinks.attempt.col.reference">Reference</TableCell>
              <TableCell align="right" data-help="sales.paymentLinks.attempt.col.amount">Amount</TableCell><TableCell data-help="sales.paymentLinks.attempt.col.status">Status</TableCell><TableCell data-help="sales.paymentLinks.attempt.col.detail">Detail</TableCell><TableCell /></TableRow></TableHead>
            <TableBody>
              {attempts.length === 0 && <TableRow><TableCell colSpan={7} align="center">Nothing needs attention.</TableCell></TableRow>}
              {attempts.map(a => (
                <TableRow key={a.id}>
                  <TableCell>{a.invoiceNumber}</TableCell><TableCell>{humanizeEnum(a.provider)}</TableCell>
                  <TableCell>{a.gatewayTrxId ?? a.merchantRef}</TableCell>
                  <TableCell align="right">{formatMoney(a.amount)}</TableCell>
                  <TableCell><Chip size="small" label={humanizeEnum(a.status)} color={STATUS_COLOR[a.status]} /></TableCell>
                  <TableCell>{a.failureReason}</TableCell>
                  <TableCell align="right">
                    {canVerify && (a.status === 'NEEDS_RECONCILIATION' || a.status === 'VERIFYING' || a.status === 'INITIATED') &&
                      <Button size="small" data-help="sales.paymentLinks.action.recheck" onClick={() => reverify(a)}>Re-check with gateway</Button>}
                    {canVerify && a.status === 'NEEDS_RECONCILIATION' &&
                      <Button size="small" data-help="sales.paymentLinks.action.resolve" onClick={() => setResolving(a)}>Mark resolved</Button>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {resolving && (
        <Dialog open onClose={() => setResolving(null)} maxWidth="xs" fullWidth data-help-page="sales.paymentLinks.resolve.page">
          <DialogTitle>Mark {resolving.invoiceNumber} payment resolved</DialogTitle>
          <DialogContent data-help-section="sales.paymentLinks.resolve.section.form" sx={{ pt: '8px !important', display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Alert severity="info">Use this once you dealt with the captured payment yourself. It will not be booked again.</Alert>
            <TextField data-help="sales.paymentLinks.resolve.receiptId" label="Manual receipt id (if you recorded it)" fullWidth value={resolveReceiptId}
              onChange={e => setResolveReceiptId(e.target.value)} />
            <TextField data-help="sales.paymentLinks.resolve.note" label="Note (e.g. refunded at the gateway)" fullWidth value={resolveNote}
              onChange={e => setResolveNote(e.target.value)} inputProps={{ maxLength: 500 }} />
          </DialogContent>
          <DialogActions>
            <Button data-help="sales.paymentLinks.resolve.close" onClick={() => setResolving(null)}>Close</Button>
            <Button variant="contained" data-help="sales.paymentLinks.resolve.confirm" onClick={resolveManually}>Mark resolved</Button>
          </DialogActions>
        </Dialog>
      )}
      {cancelling && (
        <Dialog open onClose={() => setCancelling(null)} maxWidth="xs" fullWidth data-help-page="sales.paymentLinks.cancel.page">
          <DialogTitle>Cancel the link for {cancelling.invoiceNumber}?</DialogTitle>
          <DialogContent data-help-section="sales.paymentLinks.cancel.section.form" sx={{ pt: '8px !important' }}>
            <TextField data-help="sales.paymentLinks.cancel.reason" label="Reason (optional)" fullWidth value={reason} onChange={e => setReason(e.target.value)} />
          </DialogContent>
          <DialogActions>
            <Button data-help="sales.paymentLinks.cancel.keep" onClick={() => setCancelling(null)}>Keep</Button>
            <Button color="error" variant="contained" data-help="sales.paymentLinks.cancel.confirm" onClick={cancel}>Cancel link</Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default PaymentLinks;
